/**
 * Utility functions for handling API responses and errors
 */

/**
 * Safely extract error message from API error response
 * Handles Pydantic validation errors which come as arrays of objects
 * 
 * @param {any} error - The error object or axios error response
 * @param {string} fallback - Fallback message if extraction fails
 * @returns {string} - Human readable error message
 */
export const getErrorMessage = (error, fallback = 'An error occurred') => {
  // If it's already a string, return it
  if (typeof error === 'string') {
    return error;
  }
  
  // Handle axios error response
  if (error?.response?.data) {
    const data = error.response.data;
    
    // If detail is a string, return it
    if (typeof data.detail === 'string') {
      return data.detail;
    }
    
    // If detail is an array (Pydantic validation errors)
    if (Array.isArray(data.detail)) {
      // Extract messages from validation errors
      const messages = data.detail
        .map(err => {
          if (typeof err === 'string') return err;
          if (err?.msg) return err.msg;
          if (err?.message) return err.message;
          return null;
        })
        .filter(Boolean);
      
      return messages.length > 0 ? messages.join('. ') : fallback;
    }
    
    // If detail is an object (single Pydantic error)
    if (typeof data.detail === 'object' && data.detail !== null) {
      if (data.detail.msg) return data.detail.msg;
      if (data.detail.message) return data.detail.message;
      // Try to stringify but avoid [object Object]
      try {
        const str = JSON.stringify(data.detail);
        if (str !== '{}') return str;
      } catch {
        // ignore
      }
    }
    
    // Check for message at root level
    if (typeof data.message === 'string') {
      return data.message;
    }
    
    // Check for error at root level
    if (typeof data.error === 'string') {
      return data.error;
    }
  }
  
  // Handle direct error objects
  if (error?.detail) {
    if (typeof error.detail === 'string') {
      return error.detail;
    }
    if (Array.isArray(error.detail)) {
      const messages = error.detail
        .map(err => err?.msg || err?.message || (typeof err === 'string' ? err : null))
        .filter(Boolean);
      return messages.length > 0 ? messages.join('. ') : fallback;
    }
    if (error.detail?.msg) return error.detail.msg;
  }
  
  // Handle standard Error objects
  if (error?.message && typeof error.message === 'string') {
    return error.message;
  }
  
  return fallback;
};

/**
 * Safely render content that might be an object
 * Converts objects to strings for React rendering
 * 
 * @param {any} content - The content to render
 * @returns {string} - Safe string for rendering
 */
export const safeRenderContent = (content) => {
  if (content === null || content === undefined) {
    return '';
  }
  if (typeof content === 'string') {
    return content;
  }
  if (typeof content === 'number' || typeof content === 'boolean') {
    return String(content);
  }
  if (typeof content === 'object') {
    try {
      return JSON.stringify(content, null, 2);
    } catch {
      return '[Object]';
    }
  }
  return String(content);
};
